/**
 * Retry a lazily loaded chunk once before its failure counts as a stale build.
 *
 * The 20 Sep reload (staleBundle.ts) recovers a tab holding a superseded build,
 * but the CSS chunk failures that still reached Sentry (8080-AI-12, Oct 2026)
 * were transient: the stylesheet that failed was served normally moments later,
 * and the one allowed reload either had been spent or could not help. Webpack
 * and the CSS loader both forget a chunk whose load failed, so asking again
 * re-requests it. A second failure goes on to the stale-build recovery as before.
 *
 * Patched at the chunk loader rather than per React.lazy call, so every lazy page,
 * every other dynamic import and their stylesheets get the same single retry.
 */

import { isStaleBundleError } from './staleBundle';

type EnsureChunk = (chunkId: string | number) => Promise<unknown>;

type Wait = (ms: number) => Promise<void>;

declare const __webpack_require__: { e?: EnsureChunk } | undefined;

const RETRY_DELAY_MS = 1_500;

const pause: Wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/** ``ensure`` that asks once more, after a pause, when a chunk fails to load. */
export function withChunkRetry(ensure: EnsureChunk, wait: Wait = pause, delayMs: number = RETRY_DELAY_MS): EnsureChunk {
  return (chunkId) => ensure(chunkId).catch(async (error: unknown) => {
    if (!isStaleBundleError(error)) throw error;
    await wait(delayMs);

    return ensure(chunkId);
  });
}

/** Wrap webpack's chunk loader; false outside a webpack bundle (tests). */
export function installChunkRetry(): boolean {
  if (typeof __webpack_require__ === 'undefined' || typeof __webpack_require__.e !== 'function') return false;
  __webpack_require__.e = withChunkRetry(__webpack_require__.e);

  return true;
}

installChunkRetry();
